import { useQuery } from '@tanstack/react-query';
import type { Page } from '@elutide/shared';
import { api } from '@/lib/api';
import { qk } from '@/lib/keys';
import { AgeText } from '@/components/Provenance';
import { Card, EmptyState, ErrorState, Skeleton } from '@/components';

interface ChangelogEntry {
  at: string;
  actor: string;
  summary: string;
}

/** Deploys and server changes, read from the audit log plus deploy markers. */
export function Changelog() {
  const query = useQuery({
    queryKey: qk.changelog(),
    queryFn: () => api.get<Page<ChangelogEntry>>('/settings/changelog', { query: { limit: 50 } }),
  });

  return (
    <Card title="Changelog" id="changelog">
      {query.isLoading ? <Skeleton shape="row" lines={5} label="Loading the changelog" /> : null}
      {query.isError ? (
        <ErrorState message="The changelog did not load." onRetry={() => void query.refetch()} />
      ) : null}
      {(query.data?.items ?? []).map((entry) => (
        <div className="o-kv" key={`${entry.at}-${entry.summary}`}>
          <span className="o-kv__key">{entry.summary}</span>
          <span className="o-row">
            <span className="o-muted">{entry.actor}</span>
            <AgeText at={entry.at} />
          </span>
        </div>
      ))}
      {!query.isLoading && (query.data?.items ?? []).length === 0 ? (
        <EmptyState message="Nothing deployed yet." />
      ) : null}
    </Card>
  );
}
